import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import type { RootState, AppDispatch } from '../store'
import { initiateCheckout, openPortal } from '../store/slices/subscriptionSlice'
import { useUser } from '@clerk/clerk-react'

export default function SettingsPage() {
  const dispatch = useDispatch<AppDispatch>()
  const { isPro, status, loading } = useSelector((state: RootState) => state.subscription)
  const dailyUsage = useSelector((state: RootState) => state.evaluation.dailyUsage)
  const dailyLimit = useSelector((state: RootState) => state.evaluation.dailyLimit)
  const { user } = useUser()

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Settings</h1>

      {/* Account Info */}
      <section className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account</h2>
        <div className="space-y-3">
          {user && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Name</span>
                <span className="text-gray-900 dark:text-white">{user.fullName || user.firstName || 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Email</span>
                <span className="text-gray-900 dark:text-white">{user.primaryEmailAddress?.emailAddress || 'N/A'}</span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Subscription */}
      <section className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Subscription</h2>
        <div className="space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500 dark:text-gray-400">Plan</span>
            <span className={isPro ? 'text-emerald-400 font-medium' : 'text-gray-900 dark:text-white'}>
              {isPro ? 'Pro' : 'Free'}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500 dark:text-gray-400">Status</span>
            <span className={
              status === 'active' ? 'text-emerald-400' :
              status === 'past_due' ? 'text-amber-400' :
              status === 'canceled' ? 'text-red-400' :
              'text-gray-600 dark:text-gray-300'
            }>
              {status === 'active' ? 'Active' :
               status === 'past_due' ? 'Past Due' :
               status === 'canceled' ? 'Canceled' :
               'Free Tier'}
            </span>
          </div>
          {!isPro && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500 dark:text-gray-400">Daily Evaluations</span>
              <span className="text-gray-900 dark:text-white">{dailyUsage} / {dailyLimit} used today</span>
            </div>
          )}
          {isPro && (
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500 dark:text-gray-400">Daily Evaluations</span>
              <span className="text-emerald-400">Unlimited</span>
            </div>
          )}
        </div>

        <div className="mt-6">
          {isPro ? (
            <button
              onClick={() => dispatch(openPortal())}
              disabled={loading}
              className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:text-white transition-colors border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-2"
            >
              {loading ? 'Loading...' : 'Manage Subscription'}
            </button>
          ) : (
            <button
              onClick={() => dispatch(initiateCheckout())}
              disabled={loading}
              className="btn-primary text-sm py-2 px-6"
            >
              {loading ? 'Redirecting...' : 'Upgrade to Pro — $4.99/mo'}
            </button>
          )}
        </div>
      </section>
    </div>
  )
}
